import { permissionUsage, addOnsBeyond } from '../../../shared/permission-presets.mjs';

// ENTRAID-350 "start small": the plain-language table an admin reads BEFORE the Microsoft consent page. One row per
// permission requested: its name, why onGuard needs it, which features and security checks use it, and that it is read
// only. Everything comes from portal/shared/permission-presets.mjs, so the table, the manifest and the docs never differ.

function UsedBy({ usage }) {
  const parts = [];
  if (usage.checks.length > 0) {
    parts.push(
      <span key="checks" title={usage.checks.join(', ')}>
        {usage.checks.length} security check{usage.checks.length === 1 ? '' : 's'} and the onGuard score
      </span>,
    );
  }
  if (usage.watch === 'core') parts.push(<span key="watch">Watch alerts</span>);
  else if (usage.watch) parts.push(<span key="watch">{usage.watch}</span>);
  parts.push(<span key="sources">Data: {usage.sources.map((s) => s.label).join(', ')}</span>);
  return (
    <ul className="space-y-0.5">
      {parts.map((p) => <li key={p.key}>{p}</li>)}
      {usage.license ? <li className="text-ipc-muted">Returns data only with {usage.license}.</li> : null}
    </ul>
  );
}

/**
 * @param {{ permissions: string[], granted?: string[]|null, caption?: string }} props
 * `granted` (optional): the permissions already consented, so each row says whether this consent adds it.
 */
export default function PermissionConsentTable({ permissions, granted = null, caption }) {
  const rows = [...new Set(permissions ?? [])].sort().map(permissionUsage);
  const have = Array.isArray(granted) ? new Set(granted) : null;
  return (
    <div className="space-y-2" data-testid="permission-consent-table">
      <p className="text-sm text-ipc-ink">
        {caption ?? `Microsoft will ask a Global Administrator to approve ${rows.length} permission${rows.length === 1 ? '' : 's'}.`}{' '}
        <strong>All of them are read only</strong>: onGuard can never change, create or delete anything in your directory.
      </p>
      <div className="overflow-x-auto rounded-xl border border-ipc-border">
        <table className="w-full text-left text-[13px]">
          <thead className="bg-ipc-surface text-ipc-ink">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold">Permission</th>
              <th scope="col" className="px-3 py-2 font-semibold">Why onGuard needs it</th>
              <th scope="col" className="px-3 py-2 font-semibold">What uses it</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.permission} className="border-t border-ipc-border align-top" data-permission={u.permission}>
                <th scope="row" className="min-w-[12rem] px-3 py-2 font-normal">
                  <code className="[overflow-wrap:anywhere] font-mono text-[12.5px] font-semibold text-ipc-primary">{u.permission}</code>
                  <span className="mt-1 block text-[12px] text-ipc-muted">{u.readOnly ? 'Read only' : 'Not read only'}</span>
                  {have ? (
                    <span className="mt-0.5 block text-[12px] font-semibold text-ipc-ink" data-grant-state={have.has(u.permission) ? 'granted' : 'new'}>
                      {have.has(u.permission) ? 'Already granted' : 'New in this consent'}
                    </span>
                  ) : null}
                </th>
                <td className="px-3 py-2 text-ipc-ink">{u.why}</td>
                <td className="px-3 py-2 text-ipc-ink"><UsedBy usage={u} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The permissions NOT requested now, each with what it would unlock: added later from Data collection. */
export function AddOnPermissions({ permissions }) {
  const extras = addOnsBeyond(permissions);
  if (extras.length === 0) return null;
  return (
    <div className="space-y-1" data-testid="permission-add-ons">
      <p className="text-sm font-semibold text-ipc-ink">Not requested now ({extras.length}). Add any of them later from Data collection:</p>
      <ul className="space-y-1">
        {extras.map((u) => (
          <li key={u.permission} className="text-[13px] text-ipc-ink" data-add-on={u.permission}>
            <code className="font-mono text-[12.5px] font-semibold text-ipc-primary">{u.permission}</code>{': '}
            {u.checks.length > 0 ? `${u.checks.length} security check${u.checks.length === 1 ? '' : 's'} (${u.checks.join(', ')}); ` : ''}
            {u.watch && u.watch !== 'core' ? `${u.watch}; ` : ''}
            {u.sources.map((s) => s.label).join(', ')}
            {u.license ? <span className="text-ipc-muted"> (needs {u.license})</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
